// ─── Sélecteur d'heure (modale) — Core ───────────────────────────
// Réutilisable app-wide. <SelecteurHeure visible valeur="14:30"
//   onChange={(hhmm)=>…} onFermer={…} avecEffacer />. Renvoie "HH:MM".
// Colonnes heures (0-23) + minutes (pas de 5 min). Bouton « Aucune » optionnel.
import React, { useMemo, useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import Feuille from './Feuille';

const pad = (n) => String(n).padStart(2, '0');
const HEURES = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

export default function SelecteurHeure({ visible, valeur, titre, onChange, onFermer, avecEffacer = false, min }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const titreFinal = titre ?? t('commun.choisirHeure');
  const parse = (v) => { const m = /^(\d{1,2})[:h](\d{2})$/.exec(v || ''); return m ? [+m[1], +m[2]] : [null, null]; };
  const [h, setH] = useState(parse(valeur)[0]);
  const [mn, setMn] = useState(parse(valeur)[1]);

  useEffect(() => { if (visible) { const [ph, pm] = parse(valeur); setH(ph); setMn(pm); } }, [visible, valeur]);

  // Contrainte minimale (ex. heure de fin ≥ heure de début le même jour).
  const [mnH, mnM] = parse(min);
  const minMin = mnH != null ? mnH * 60 + (mnM ?? 0) : null;
  const heureDesactivee = (x) => minMin != null && (x * 60 + 59) < minMin;   // toute l'heure avant le min
  const minuteDesactivee = (x) => minMin != null && h != null && (h * 60 + x) < minMin;

  const valide = h != null && (minMin == null || (h * 60 + (mn ?? 0)) >= minMin);
  // Disquette grisée si la valeur n'a pas changé depuis l'ouverture (principe app-wide).
  const [oh, om] = parse(valeur);
  const inchange = h === oh && (mn ?? 0) === (om ?? 0);
  const action = { icone: '💾', label: t('commun.valider'), disabled: !valide || inchange,
    onPress: () => { if (valide) { onChange?.(`${pad(h)}:${pad(mn ?? 0)}`); onFermer?.(); } } };
  const boutons = avecEffacer ? (
    <TouchableOpacity onPress={() => { onChange?.(''); onFermer?.(); }} hitSlop={HIT} accessibilityLabel={t('commun.aucuneHeure')}>
      <Text style={s.effacer}>{t('commun.aucune')}</Text>
    </TouchableOpacity>
  ) : null;

  return (
    <Feuille visible={visible} titre={titreFinal} emoji="🕒" hauteur={0.8} sansScroll onFermer={onFermer} action={action} boutons={boutons}>
      <Text style={s.apercu}>{valide ? `${pad(h)}:${pad(mn ?? 0)}` : t('commun.choisisHeure')}</Text>
      <View style={s.cols}>
        <View style={s.col}>
          <Text style={s.colTitre}>{t('commun.heures')}</Text>
          <ScrollView style={s.colScroll} showsVerticalScrollIndicator={false}>
            <View style={s.wrap}>
              {HEURES.map(x => { const off = heureDesactivee(x); return (
                <TouchableOpacity key={x} disabled={off} style={[s.pastille, h === x && s.pastilleOn, off && s.pastilleOff]} onPress={() => setH(x)}>
                  <Text style={[s.pastilleTxt, h === x && s.pastilleTxtOn, off && s.pastilleTxtOff]}>{pad(x)}</Text>
                </TouchableOpacity>
              ); })}
            </View>
          </ScrollView>
        </View>
        <View style={s.col}>
          <Text style={s.colTitre}>{t('commun.minutes')}</Text>
          <ScrollView style={s.colScroll} showsVerticalScrollIndicator={false}>
            <View style={s.wrap}>
              {MINUTES.map(x => { const off = minuteDesactivee(x); return (
                <TouchableOpacity key={x} disabled={off} style={[s.pastille, (mn ?? 0) === x && s.pastilleOn, off && s.pastilleOff]} onPress={() => setMn(x)}>
                  <Text style={[s.pastilleTxt, (mn ?? 0) === x && s.pastilleTxtOn, off && s.pastilleTxtOff]}>{pad(x)}</Text>
                </TouchableOpacity>
              ); })}
            </View>
          </ScrollView>
        </View>
      </View>
    </Feuille>
  );
}

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

const makeStyles = (UI) => StyleSheet.create({
  cols: { flexDirection: 'row', gap: 14, flex: 1 },
  col: { flex: 1 },
  colTitre: { fontSize: 12, fontWeight: '800', color: UI.muted, marginBottom: 8, textAlign: 'center' },
  colScroll: { flex: 1 },
  wrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, justifyContent: 'center' },
  pastille: { width: 46, height: 40, borderRadius: 10, backgroundColor: UI.champ, borderWidth: 0.5, borderColor: UI.champBord, alignItems: 'center', justifyContent: 'center' },
  pastilleOn: { backgroundColor: UI.accent, borderColor: UI.accent },
  pastilleOff: { opacity: 0.32 },
  pastilleTxt: { fontSize: 15, fontWeight: '700', color: UI.ink },
  pastilleTxtOn: { color: '#fff', fontWeight: '800' },
  pastilleTxtOff: { color: UI.faint },
  apercu: { fontSize: 20, fontWeight: '800', color: UI.accent, textAlign: 'center', marginTop: 10, marginBottom: 14 },
  effacer: { fontSize: 13, color: UI.muted, fontWeight: '700' },
});
